/**
 * What `html()` reads of a page's visual CSS: colour, type, case. Pure functions on CSS values, no scene and no layout: the compiler
 * resolves an element's cascade with these, and the block tools turn the result into Style bindings and Text options.
 */
import { RUNGS, TYPE_RAMP, type Rung } from './design-tokens';

// ── Colour ──────────────────────────────────────────────

export interface Rgba { hex: string; a: number }

const NAMED: Record<string, string> = {
    black: '#000000', white: '#ffffff', red: '#ff0000', green: '#008000', blue: '#0000ff', yellow: '#ffff00', orange: '#ffa500', purple: '#800080',
    gray: '#808080', grey: '#808080', silver: '#c0c0c0', navy: '#000080', teal: '#008080', maroon: '#800000', lime: '#00ff00', aqua: '#00ffff',
    fuchsia: '#ff00ff', olive: '#808000', pink: '#ffc0cb', gold: '#ffd700', ivory: '#fffff0', beige: '#f5f5dc', brown: '#a52a2a', crimson: '#dc143c',
};
const h2 = (n: number) => Math.round(Math.max(0, Math.min(255, n))).toString(16).padStart(2, '0');

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
    const k = (n: number) => (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    return [f(0) * 255, f(8) * 255, f(4) * 255];
}

/** A CSS colour as `#RRGGBB` plus alpha; null for anything that is not one (var(), gradients, currentcolor, a keyword). */
export function parseColor(value: string | undefined): Rgba | null {
    if (value === undefined) return null;
    const v = value.trim().toLowerCase();
    if (v === 'transparent') return { hex: '#000000', a: 0 };
    if (NAMED[v]) return { hex: NAMED[v], a: 1 };
    let m = /^#([0-9a-f]{3,8})$/.exec(v);
    if (m) {
        const d = m[1];
        if (d.length === 3 || d.length === 4) return { hex: `#${d[0]}${d[0]}${d[1]}${d[1]}${d[2]}${d[2]}`, a: d.length === 4 ? parseInt(d[3] + d[3], 16) / 255 : 1 };
        if (d.length === 6) return { hex: `#${d}`, a: 1 };
        if (d.length === 8) return { hex: `#${d.slice(0, 6)}`, a: parseInt(d.slice(6), 16) / 255 };
        return null;
    }
    m = /^(rgba?|hsla?)\(\s*([^)]+)\)$/.exec(v);
    if (!m) return null;
    const parts = m[2].split(/[\s,/]+/).filter(Boolean);
    if (parts.length < 3) return null;
    const alpha = parts[3] === undefined ? 1 : parts[3].endsWith('%') ? parseFloat(parts[3]) / 100 : parseFloat(parts[3]);
    if (!Number.isFinite(alpha)) return null;
    const a = Math.max(0, Math.min(1, alpha));
    if (m[1].startsWith('rgb')) {
        const ch = parts.slice(0, 3).map(p => p.endsWith('%') ? parseFloat(p) * 2.55 : parseFloat(p));
        return ch.some(c => !Number.isFinite(c)) ? null : { hex: `#${h2(ch[0])}${h2(ch[1])}${h2(ch[2])}`, a };
    }
    const hue = parseFloat(parts[0]); const s = parseFloat(parts[1]) / 100; const l = parseFloat(parts[2]) / 100;
    if (![hue, s, l].every(Number.isFinite)) return null;
    const [r, g, b] = hslToRgb(((hue % 360) + 360) % 360, s, l);
    return { hex: `#${h2(r)}${h2(g)}${h2(b)}`, a };
}

/** The first colour in a `background` shorthand or gradient (its first stop), or null. */
export function firstColor(value: string): Rgba | null {
    const tokens = value.match(/#[0-9a-f]{3,8}\b|(?:rgba?|hsla?)\([^)]*\)|\b[a-z]+\b/gi) ?? [];
    for (const t of tokens) {
        const c = parseColor(t);
        if (c && c.a > 0) return c;
    }
    return null;
}

const rgb = (hex: string): [number, number, number] => [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
/** Perceptual-ish distance ("redmean"), so a dark teal is near the dark role, not the blue one. */
export function colorDistance(a: string, b: string): number {
    const [r1, g1, b1] = rgb(a); const [r2, g2, b2] = rgb(b);
    const rm = (r1 + r2) / 2;
    return Math.sqrt((2 + rm / 256) * (r1 - r2) ** 2 + 4 * (g1 - g2) ** 2 + (2 + (255 - rm) / 256) * (b1 - b2) ** 2);
}
/** The key of the candidate nearest to `hex`. */
export function nearestColor(hex: string, candidates: Readonly<Record<string, string>>): string {
    let best = ''; let bestD = Infinity;
    for (const [key, value] of Object.entries(candidates)) {
        const d = colorDistance(hex, value);
        if (d < bestD) { best = key; bestD = d; }
    }
    return best;
}

// ── Type ────────────────────────────────────────────────

/** A CSS length in px; `rem` is 16, `em` and `%` are of `parentPx`. Null when it is not a length. */
export function toPx(value: string | undefined, parentPx: number): number | null {
    if (value === undefined) return null;
    const m = /^(-?\d*\.?\d+)\s*(px|rem|em|%|pt)?$/.exec(value.trim().toLowerCase());
    if (!m) return null;
    const v = parseFloat(m[1]);
    switch (m[2]) {
        case 'rem': return v * 16;
        case 'em': return v * parentPx;
        case '%': return v / 100 * parentPx;
        case 'pt': return v * 4 / 3;
        default: return v;
    }
}

/** The rung whose size, against body2 as the body, sits nearest `px` against `basePx` on a log scale: sizes keep their ratios. */
export function rungForSize(px: number, basePx: number): Rung {
    const body = TYPE_RAMP.find(r => r.rung === 'body2')!.du;
    const want = Math.log(px / basePx);
    let best: Rung = 'body2'; let bestD = Infinity;
    for (const step of TYPE_RAMP) {
        const d = Math.abs(Math.log(step.du / body) - want);
        if (d < bestD - 1e-9) { best = step.rung; bestD = d; }
    }
    return RUNGS.includes(best) ? best : 'body2';
}

export type FontCategory = 'sans' | 'serif' | 'mono' | 'display' | 'script';
const SERIF = /serif|bodoni|fraunces|playfair|italiana|cormorant|lora|merriweather|garamond|georgia|times|didot|baskerville|dm serif|libre|crimson|prata|cinzel|abril/i;
const MONO = /mono|courier|consolas|menlo|code|typewriter/i;
const SCRIPT = /script|cursive|caveat|pacifico|dancing|handwrit|brush|satisfy|lobster/i;
const DISPLAY = /impact|oswald|bebas|anton|bungee|league gothic|archivo black|fjalla|display|condensed/i;

/** The category of a font stack: by a known name first, then by the generic keyword the stack ends with. */
export function fontCategory(stack: string): FontCategory {
    const names = stack.split(',').map(s => s.trim().replace(/^["']|["']$/g, ''));
    const named = names.find(n => !/^(serif|sans-serif|monospace|cursive|fantasy|system-ui|ui-[a-z-]+|-apple-system|inherit)$/i.test(n));
    if (named) {
        if (MONO.test(named)) return 'mono';
        if (SCRIPT.test(named)) return 'script';
        // The word "Display" alone does not make a face condensed: "Playfair Display" is a serif.
        if (DISPLAY.test(named.replace(/display/gi, '')) && !/serif/i.test(named)) return 'display';
        if (SERIF.test(named) && !/sans/i.test(named)) return 'serif';
        if (DISPLAY.test(named)) return 'display';
    }
    const generic = names.map(n => n.toLowerCase()).filter(n => /^(serif|sans-serif|monospace|cursive|fantasy)$/.test(n)).pop();
    if (generic === 'serif') return 'serif';
    if (generic === 'monospace') return 'mono';
    if (generic === 'cursive') return 'script';
    if (generic === 'fantasy') return 'display';
    return 'sans';
}

/** The bundled family nearest each category; sans keeps the Style's own font, so it has none. */
export const CATEGORY_FALLBACK: Readonly<Record<FontCategory, string | undefined>> = {
    sans: undefined, serif: 'Source Serif 4', mono: 'JetBrains Mono', display: 'Oswald', script: 'Caveat',
};

export interface FontChoice {
    /** The family to set literally, or undefined to keep the Style's font. */
    family?: string;
    /** Set when the first named family is not available and a stand-in was used. */
    note?: string;
}

/** Pick a family for a CSS font stack from what is available (bundled plus hosted). */
export function chooseFont(stack: string, available: readonly string[]): FontChoice {
    const have = new Map(available.map(f => [f.toLowerCase(), f]));
    const names = stack.split(',').map(s => s.trim().replace(/^["']|["']$/g, ''));
    for (const n of names) {
        const hit = have.get(n.toLowerCase());
        if (hit) return hit === 'Roboto' ? {} : { family: hit };
    }
    const first = names.find(n => !/^(serif|sans-serif|monospace|cursive|fantasy|system-ui|ui-[a-z-]+|-apple-system|inherit)$/i.test(n));
    const category = fontCategory(stack);
    const stand = CATEGORY_FALLBACK[category];
    const standIn = stand && have.has(stand.toLowerCase()) ? stand : undefined;
    return {
        ...(standIn ? { family: standIn } : {}),
        ...(first ? { note: `font "${first}" is not hosted; ${standIn ? `set in ${standIn}, the nearest ${category} face` : 'the Style font was kept'}.` } : {}),
    };
}

export function applyCase(text: string, transform: string | undefined): string {
    if (transform === 'uppercase') return text.toUpperCase();
    if (transform === 'lowercase') return text.toLowerCase();
    if (transform === 'capitalize') return text.replace(/(^|\s)(\p{L})/gu, (_m, s: string, c: string) => s + c.toUpperCase());
    return text;
}

// ── Box edges: border, radius, shadow ───────────────────

export interface Edge { width: number; color: Rgba }
/** A uniform border from `border` or `border-width` with `border-color`; one side alone is a divider, not a rim, and is not drawn. null when there is none. */
export function parseBorder(d: Record<string, string>, fallbackInk: Rgba | undefined): Edge | null {
    const sides = ['border'];
    for (const key of sides) {
        const v = d[key];
        if (v === undefined || v === 'none' || v === '0') continue;
        const noColor = v.replace(/(?:rgba?|hsla?)\([^)]*\)/g, ' ').replace(/#[0-9a-f]{3,8}\b/g, ' ');
        const width = /(-?\d*\.?\d+)(px|rem|em)?/.exec(noColor);
        const px = width ? parseFloat(width[1]) * (width[2] === 'rem' || width[2] === 'em' ? 16 : 1) : 1;
        if (!(px > 0) || /\b(none|hidden)\b/.test(v)) continue;
        const color = (v.match(/(?:rgba?|hsla?)\([^)]*\)|#[0-9a-f]{3,8}\b|\b[a-z]+\b/gi) ?? []).map(parseColor).find(c => c !== null) ?? null;
        const c = color ?? (d['border-color'] ? parseColor(d['border-color']) : null) ?? fallbackInk;
        if (c && c.a > 0) return { width: px, color: c };
    }
    const w = d['border-width'];
    const c = d['border-color'] ? parseColor(d['border-color']) : null;
    if (w !== undefined && c && c.a > 0 && d['border-style'] !== 'none') {
        const px = toPx(w.split(/\s+/)[0], 16);
        if (px && px > 0) return { width: px, color: c };
    }
    return null;
}

/** A corner radius in px (a `%` is of `refPx`); the first value of a shorthand. */
export function parseRadiusPx(value: string | undefined, refPx: number): number | null {
    if (value === undefined) return null;
    const first = value.split(/[\s/]+/)[0];
    const px = toPx(first, refPx);
    return px !== null && px >= 0 ? px : null;
}

export interface Shadow { x: number; y: number; blur: number; spread: number; color: Rgba; inset: boolean }
/** The shadows of a `box-shadow` or `text-shadow` value. */
export function parseShadows(value: string | undefined): Shadow[] {
    if (value === undefined || value === 'none') return [];
    const parts: string[] = []; let depth = 0, at = 0;
    for (let i = 0; i < value.length; i++) {
        if (value[i] === '(') depth++;
        else if (value[i] === ')') depth--;
        else if (value[i] === ',' && depth === 0) { parts.push(value.slice(at, i)); at = i + 1; }
    }
    parts.push(value.slice(at));
    const out: Shadow[] = [];
    for (const part of parts) {
        const inset = /\binset\b/.test(part);
        const color = firstColor(part.replace(/inset/g, ''));
        const nums = [...part.replace(/(?:rgba?|hsla?)\([^)]*\)/g, ' ').matchAll(/(-?\d*\.?\d+)(px|rem|em)?/g)].map(m => parseFloat(m[1]) * (m[2] === 'rem' || m[2] === 'em' ? 16 : 1));
        if (nums.length < 2 || !color) continue;
        out.push({ x: nums[0], y: nums[1], blur: nums[2] ?? 0, spread: nums[3] ?? 0, color, inset });
    }
    return out;
}

/** One-sided borders (`border-top`, `border-bottom`, `border-left`, `border-right`): dividers and underlines, drawn as thin bars. */
export function parseSideBorders(d: Record<string, string>, fallbackInk: Rgba | undefined): { side: 'top' | 'bottom' | 'left' | 'right'; width: number; color: Rgba }[] {
    const out: { side: 'top' | 'bottom' | 'left' | 'right'; width: number; color: Rgba }[] = [];
    if (d.border !== undefined && d.border !== 'none') return out;
    for (const side of ['top', 'bottom', 'left', 'right'] as const) {
        const e = parseBorder({ border: d[`border-${side}`] ?? 'none' }, fallbackInk);
        if (e) out.push({ side, ...e });
    }
    return out;
}
